import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HandCoins, FileWarning, Banknote, PiggyBank, Landmark, AlertTriangle, ClipboardList, Receipt, Wallet, Ship, ArrowRight } from "lucide-react";
import api, { money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";

const totalsStr = (t) => { const e = Object.entries(t || {}); return e.length ? e.map(([c, v]) => money(c, v)).join(" · ") : money("GTQ", 0); };

const QUICK = [
  { label: "Recibos y Cobros", path: "/recibos", icon: HandCoins, perm: "receipts:view" },
  { label: "Pago a Proveedores", path: "/pagos", icon: Banknote, perm: "payments:view" },
  { label: "Caja Chica", path: "/caja-chica", icon: PiggyBank, perm: "pettycash:view" },
  { label: "Facturas", path: "/facturas", icon: Receipt, perm: "invoices:view" },
  { label: "Estados de Cuenta", path: "/estados-cuenta", icon: Wallet, perm: "statements:view" },
  { label: "Importaciones", path: "/importaciones", icon: Ship, perm: "imports:view" },
];

const ALERT_STYLE = {
  danger: "border-l-red-500 bg-red-500/10 text-red-500",
  warn: "border-l-amber-500 bg-amber-500/10 text-amber-600",
  info: "border-l-primary bg-primary/5 text-foreground",
};

function DayCard({ icon: Icon, label, value, sub, subDanger, accent, testId }) {
  return (
    <Card className="rounded-sm border shadow-none p-5 space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground"><Icon className={`h-4 w-4 ${accent || ""}`} /><span className="text-xs font-bold uppercase tracking-widest">{label}</span></div>
      <p className="text-2xl font-heading font-black leading-none">{value}</p>
      {sub != null && <p className={`text-xs ${subDanger ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{sub}</p>}
    </Card>
  );
}

export const AuxiliarPanel = () => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/auxiliar").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;

  const quick = QUICK.filter((q) => can(q.perm));

  return (
    <section className="space-y-4" data-testid="auxiliar-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ClipboardList className="h-5 w-5 text-primary" /> Resumen de trabajo de hoy</h3>
        <span className="text-xs text-muted-foreground">{d.date}</span>
      </div>

      {d.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="auxiliar-alerts">
          {d.alerts.map((a, i) => (
            <Card key={i} className={`rounded-sm border-l-4 shadow-none p-3 flex items-center gap-2 text-sm ${ALERT_STYLE[a.level] || ALERT_STYLE.info}`} data-testid={`aux-alert-${i}`}>
              <AlertTriangle className="h-4 w-4 shrink-0" /> <span>{a.text}</span>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <DayCard icon={HandCoins} label="Ingresos de hoy" value={totalsStr(d.ingresos_hoy)} sub={`${d.recibos_hoy} recibo(s) · ${d.docs_today} registro(s)`} accent="text-green-600" testId="aux-ingresos" />
        <DayCard icon={FileWarning} label="Cuentas por cobrar" value={totalsStr(d.receivables)} sub={`${d.open_invoices} factura(s) abierta(s)`} testId="aux-receivables" />
        <DayCard icon={AlertTriangle} label="Saldo vencido" value={totalsStr(d.receivables_overdue)} sub={`${d.overdue_count} vencida(s)`} subDanger={d.overdue_count > 0} accent="text-red-600" testId="aux-overdue" />
        <DayCard icon={Banknote} label="Cuentas por pagar" value={totalsStr(d.payables)} sub={`${d.payable_count} documento(s)`} testId="aux-payables" />
        <DayCard icon={PiggyBank} label="Caja chica" value={money(d.petty.currency, d.petty.balance)} sub={`Asignado ${money(d.petty.currency, d.petty.assigned)} · ${d.petty.moves_today} mov. hoy`} testId="aux-petty" />
        <DayCard icon={Landmark} label="Saldos bancarios" value={totalsStr(d.bank_totals)} testId="aux-bank" />
      </div>

      <Card className="rounded-sm border shadow-none p-5" data-testid="aux-quick-access">
        <h4 className="font-heading font-bold mb-4 text-sm uppercase tracking-widest text-muted-foreground">Accesos rápidos</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {quick.map((q) => { const Icon = q.icon; return (
            <Button key={q.path} variant="outline" className="rounded-sm justify-between h-11 text-sm font-semibold group" onClick={() => navigate(q.path)} data-testid={`aux-quick-${q.path.slice(1)}`}>
              <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /> {q.label}</span>
              <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Button>
          ); })}
        </div>
      </Card>
    </section>
  );
};
